import AssessmentIcon from '@mui/icons-material/Assessment';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import Autocomplete from '@mui/material/Autocomplete';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DataGrid,
  GridColDef,
  GridFilterModel,
  GridRowParams,
} from '@mui/x-data-grid';
import { toast } from 'material-react-toastify';
import { ExperimentDescription } from '../../interfaces/Experiments';
import { getAllExperiments } from '../../services/experiments';
import { experimentsUIUrl } from '../../variables/Pages';

export default function ExperimentUI() {
  const navigate = useNavigate();
  const [allExperiments, setAllExperiments] = useState<ExperimentDescription[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [debouncedFilterModel, setDebouncedFilterModel] =
    useState<GridFilterModel>({
      items: [],
    });

  // Validation function for date filters
  const isValidDateFilter = useCallback((filterItem: any): boolean => {
    if (filterItem.field !== 'latestUpdatedAt') {
      return true; // Non-date filters are always valid
    }

    if (!filterItem.value) {
      return true; // Empty values are valid (no filter)
    }

    const dateValue = new Date(filterItem.value);
    return (
      !Number.isNaN(dateValue.getTime()) &&
      dateValue instanceof Date &&
      dateValue.toString() !== 'Invalid Date'
    );
  }, []);

  // Debounce filter model changes
  useEffect(() => {
    const timer = setTimeout(() => {
      // Validate all date filters before applying
      const validFilters = filterModel.items.filter((item) =>
        isValidDateFilter(item),
      );

      if (validFilters.length !== filterModel.items.length) {
        // Some filters were invalid, update the filter model to remove them
        setFilterModel({ items: validFilters });
        toast.error('Invalid date filter value. Please enter a valid date.');
        return;
      }

      setDebouncedFilterModel(filterModel);
    }, 500);

    return () => clearTimeout(timer);
  }, [filterModel, isValidDateFilter]);

  const filteredExperiments = allExperiments.filter((experiment) =>
    experiment.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  useEffect(() => {
    const fetchExperiments = async () => {
      try {
        const result = await getAllExperiments();
        setAllExperiments(result.experiments);
      } catch (error) {
        toast.error(`Error fetching experiments: ${error}`);
      } finally {
        setLoading(false);
      }
    };

    fetchExperiments();
  }, []);

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', minWidth: 250 },
    { field: 'enabled', headerName: 'Enabled', width: 120 },
    { field: 'description', headerName: 'Description', flex: 1 },
    {
      field: 'latestUpdatedAt',
      headerName: 'Latest Updated At',
      width: 180,
      type: 'date',
      renderHeader: () => (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
            Latest Updated At
          </Typography>
          <Typography
            variant='caption'
            sx={{ fontSize: '0.7rem', color: 'text.disabled', lineHeight: 1 }}
          >
            {Intl.DateTimeFormat().resolvedOptions().timeZone}
          </Typography>
        </Box>
      ),
      valueGetter: (value, row) => {
        if (row.latestUpdatedAt) {
          return new Date(row.latestUpdatedAt);
        }
        return null;
      },
      renderCell: (params) => {
        if (params.row.latestUpdatedAt) {
          return new Date(params.row.latestUpdatedAt).toLocaleDateString(
            navigator.language || 'en-US',
            {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: navigator.language?.startsWith('en'),
            },
          );
        }
        return '-';
      },
    },
  ];

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Paper sx={{ padding: 3, margin: 2, maxWidth: '1200px', width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
          }}
        >
          <h2>All Experiments</h2>
          <Button
            color='primary'
            onClick={() => navigate(`${experimentsUIUrl}/analysis`)}
            startIcon={<AssessmentIcon />}
            variant='contained'
          >
            Experiment Analysis
          </Button>
        </Box>
        <Autocomplete
          freeSolo
          onChange={(_event: any, value: any, reason: any) => {
            if (reason === 'selectOption') {
              navigate(`${experimentsUIUrl}/${value}`);
            }
          }}
          onInputChange={(_event: any, newInputValue: any) => {
            setSearchTerm(newInputValue);
          }}
          options={allExperiments.map((experiment) => experiment.name)}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextField
              sx={{ margin: '0px 0px 5px 5px' }}
              {...params}
              fullWidth
              label='Search Experiments'
              onChange={(e) => setSearchTerm(e.target.value)}
              variant='filled'
            />
          )}
          sx={{ width: '80%', margin: '10px auto 10px ajut' }}
        />
        <DataGrid
          columns={columns}
          getRowId={(row) => row.name}
          loading={loading}
          onRowClick={(params: GridRowParams, event: React.MouseEvent) => {
            const url = `${experimentsUIUrl}/${params.row.name}`;
            // Check for cmd+click (Mac) or ctrl+click (Windows/Linux)
            if (event.metaKey || event.ctrlKey) {
              window.open(url, '_blank', 'noopener,noreferrer');
            } else {
              navigate(url);
            }
          }}
          rows={filteredExperiments}
          filterMode='client'
          filterModel={debouncedFilterModel}
          onFilterModelChange={(model) => setFilterModel(model)}
          initialState={{
            filter: {
              filterModel: {
                items: [],
              },
            },
          }}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            },
          }}
        />
        {loading && <CircularProgress />}
      </Paper>
    </Box>
  );
}
